import { Plus, Trash2 } from "lucide-react";
import React from "react";
import { useFieldArray, UseFormReturn } from "react-hook-form";

import {
  Button,
  Card,
  DatePicker,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  RequiredLabel,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components";
import RichTextEditor from "@/components/rich-text-editor";
import { RealCurrencyInput } from "@/components/ui/real-currency-input";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/components/ui/use-toast";
import { useI18n } from "@/context/i18n";
import { useInsuranceCompanies } from "@/pages/insurance-companies/insurance-companies.hooks";
import { EProductType } from "@/pages/products/products.type";

import { TProductFormData } from "../../products.schema";
import { SoulhubExternalProductIdField } from "../soulhub-external-product-id-field";

export interface IProductsFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  form: UseFormReturn<TProductFormData>;
  onSubmit: (values: TProductFormData) => void;
  isEditMode?: boolean;
}

export const InsuranceFuneralForm: React.FC<IProductsFormProps> = ({
  open,
  form,
  onSubmit,
  isEditMode,
}) => {
  const { t } = useI18n();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = React.useState("data");
  const [planNameErrors, setPlanNameErrors] = React.useState<{
    [key: number]: string;
  }>({});

  const {
    fields: capitalRangeFields,
    append: appendCapitalRange,
    remove: removeCapitalRange,
  } = useFieldArray({
    control: form.control,
    name: "capital_ranges",
  });

  // Validação por etapa
  const validateTab = React.useCallback(
    async (tab: string): Promise<boolean> => {
      const fieldsByTab: Record<string, string[]> = {
        data: [
          "name",
          "insurance_company_id",
          "type_of_vigency",
          "policy_start_date",
          "policy_end_date",
          "min_permitted_age",
          "max_permitted_age",
        ],
        price: ["capital_ranges"],
        integration: [],
      };

      const fieldsToValidate = fieldsByTab[tab] || [];
      if (fieldsToValidate.length === 0) return true;

      const result = await form.trigger(fieldsToValidate as any);
      return result;
    },
    [form]
  );

  const handleTabChange = React.useCallback(
    async (value: string) => {
      const tabOrder = ["data", "price", "integration"];
      const currentIndex = tabOrder.indexOf(activeTab);
      const newIndex = tabOrder.indexOf(value);

      // Permite voltar para tabs anteriores sem validar
      if (newIndex < currentIndex) {
        setActiveTab(value);
        return;
      }

      // Valida antes de avançar
      const isValid = await validateTab(activeTab);
      if (isValid) {
        setActiveTab(value);
      } else {
        // Mostra mensagem de erro
        form.trigger(
          activeTab === "data"
            ? ([
                "name",
                "insurance_company_id",
                "type_of_vigency",
                "policy_start_date",
                "policy_end_date",
                "min_permitted_age",
                "max_permitted_age",
              ] as any)
            : (["capital_ranges"] as any)
        );
      }
    },
    [activeTab, validateTab, form]
  );
  const addInput = () => {
    appendCapitalRange({
      capital_value: 0,
      net_price: 0,
      minimum_price: 0,
      maximum_price: 0,
      description: "",
      plan_name: "",
    } as any);
  };

  const removeInput = (index: number) => {
    if (capitalRangeFields.length > 1) {
      removeCapitalRange(index);
      // Remove error when field is removed
      setPlanNameErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[index];
        return newErrors;
      });
    }
  };

  const validatePlanName = (index: number, value: string) => {
    if (!value || value.trim() === "") {
      setPlanNameErrors((prev) => ({
        ...prev,
        [index]: "Nome do plano é obrigatório",
      }));
      return false;
    } else {
      setPlanNameErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[index];
        return newErrors;
      });
      return true;
    }
  };

  const { insuranceCompanies } = useInsuranceCompanies();

  const handleInvalidSubmit = React.useCallback(
    (errors: Record<string, unknown>) => {
      const flattenErrorKeys = (obj: Record<string, unknown>, prefix = ""): string[] => {
        return Object.entries(obj).flatMap(([key, value]) => {
          const path = prefix ? `${prefix}.${key}` : key;
          // react-hook-form FieldError: { message, type, ref }
          if (
            value &&
            typeof value === "object" &&
            !Array.isArray(value) &&
            "message" in (value as Record<string, unknown>) &&
            "type" in (value as Record<string, unknown>)
          ) {
            return [prefix || key];
          }
          if (value && typeof value === "object" && !Array.isArray(value)) {
            return flattenErrorKeys(value as Record<string, unknown>, path);
          }
          return [path];
        });
      };

      const errorKeys = flattenErrorKeys(errors);

      if (errors?.name || errors?.insurance_company_id || errors?.type_of_vigency) {
        setActiveTab("data");
      } else if (errors?.capital_ranges) {
        setActiveTab("price");
      } else {
        setActiveTab("integration");
      }

      toast({
        title: t("generic.error"),
        description:
          errorKeys.length > 0
            ? `Campos obrigatórios: ${errorKeys.join(", ")}`
            : "Preencha os campos obrigatórios antes de salvar.",
        variant: "destructive",
      });
    },
    [toast, t]
  );

  React.useEffect(() => {
    if (open) {
      form.setValue("type", EProductType.INSURANCE_ASSISTANCE);
      form.setValue("type_of_vigency", "lifetime");
      // Define valores padrão para campos de comissão
      form.setValue("commission_lifetime_other_installments", 0);
      form.setValue("pro_labore_lifetime_other_installments", 0);
      setActiveTab("data");
    }
  }, [open, form]);

  if (open) {
    return (
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit, handleInvalidSubmit)}
          className="space-y-4"
          noValidate
        >
          <Tabs value={activeTab} onValueChange={handleTabChange}>
            <TabsList>
              <TabsTrigger value="data">
                1. {t("products.title.tab.data")}
              </TabsTrigger>
              <TabsTrigger value="price">
                2. {t("products.title.tab.plans")}
              </TabsTrigger>
              <TabsTrigger value="integration">
                3. {t("products.title.tab.integration")}
              </TabsTrigger>
            </TabsList>
            <TabsContent value="data">
              <Card className="p-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <RequiredLabel htmlFor="name" required>
                          {t("products.form.fields.name")}
                        </RequiredLabel>
                        <FormControl>
                          <Input {...field} id="name" placeholder=" " />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <SoulhubExternalProductIdField form={form} />
                  <FormField
                    control={form.control}
                    name="insurance_company_id"
                    render={({ field }) => (
                      <FormItem>
                        <RequiredLabel htmlFor="insurance_company_id" required>
                          {t("products.form.fields.guarantor")}
                        </RequiredLabel>
                        <FormControl>
                          <Select
                            onValueChange={field.onChange}
                            value={field.value || ""}
                          >
                            <SelectTrigger className="w-[100%]">
                              <SelectValue
                                placeholder={t(
                                  "products.form.fields.insurance_company_id.placeholder"
                                )}
                              />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectGroup>
                                {insuranceCompanies.map((company) => (
                                  <SelectItem
                                    key={company.id}
                                    value={company.id || ""}
                                  >
                                    {company.name} - {company.cnpj}
                                  </SelectItem>
                                ))}
                              </SelectGroup>
                            </SelectContent>
                          </Select>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="policy_start_date"
                    render={({ field }) => (
                      <FormItem>
                        <RequiredLabel htmlFor="policy_start_date" required>
                          {t("products.form.fields.policy_start_date")}
                        </RequiredLabel>
                        <FormControl>
                          <DatePicker
                            value={field.value || undefined}
                            onChange={(date) => {
                              field.onChange(date);
                            }}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="policy_end_date"
                    render={({ field }) => (
                      <FormItem>
                        <RequiredLabel htmlFor="policy_end_date" required>
                          {t("products.form.fields.policy_end_date")}
                        </RequiredLabel>
                        <FormControl>
                          <DatePicker
                            value={field.value || undefined}
                            onChange={(date) => {
                              field.onChange(date);
                            }}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="min_permitted_age"
                    render={({ field }) => (
                      <FormItem>
                        <RequiredLabel htmlFor="min_permitted_age" required>
                          {t("products.form.fields.min_permitted_age")}
                        </RequiredLabel>
                        <FormControl>
                          <Input
                            {...field}
                            id="min_permitted_age"
                            type="number"
                            placeholder=" "
                            value={field.value ?? ""}
                            onChange={(e) => {
                              const value =
                                e.target.value === ""
                                  ? undefined
                                  : Number(e.target.value);
                              field.onChange(value);
                            }}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="max_permitted_age"
                    render={({ field }) => (
                      <FormItem>
                        <RequiredLabel htmlFor="max_permitted_age" required>
                          {t("products.form.fields.max_permitted_age")}
                        </RequiredLabel>
                        <FormControl>
                          <Input
                            {...field}
                            id="max_permitted_age"
                            type="number"
                            placeholder=" "
                            value={field.value ?? ""}
                            onChange={(e) => {
                              const value =
                                e.target.value === ""
                                  ? undefined
                                  : Number(e.target.value);
                              field.onChange(value);
                            }}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {t("products.form.fields.description")}
                      </FormLabel>
                      <FormControl>
                        <RichTextEditor
                          content={field.value || ""}
                          onChange={field.onChange}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="insurance_coverages"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {t("products.form.fields.coverage")}
                      </FormLabel>
                      <FormControl>
                        <RichTextEditor
                          content={field.value || ""}
                          onChange={field.onChange}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="insurance_benefits"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {t("products.form.fields.benefits")}
                      </FormLabel>
                      <FormControl>
                        <RichTextEditor
                          content={field.value || ""}
                          onChange={field.onChange}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="general_conditions"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel htmlFor="general_conditions">
                        {t("products.form.fields.general_conditions")}
                      </FormLabel>
                      <FormControl>
                        <RichTextEditor
                          content={field.value || ""}
                          onChange={field.onChange}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </Card>
            </TabsContent>
            <TabsContent value="price">
              <Card className="p-4">
                <div className="flex flex-wrap gap-4 mb-4">
                  {capitalRangeFields.map((fieldItem, index) => (
                    <Card
                      key={fieldItem.id}
                      className="p-4 flex flex-col gap-4 w-full min-w-[1200px] h-auto"
                    >
                      <div className="flex items-center justify-between">
                        <h3 className="font-medium">{index + 1}º</h3>
                        {capitalRangeFields.length > 1 && (
                          <Button
                            type="button"
                            variant="destructive"
                            size="sm"
                            onClick={() => removeInput(index)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <FormField
                          control={form.control}
                          name={`capital_ranges.${index}.net_price`}
                          render={({ field }) => (
                            <FormItem>
                              <RequiredLabel
                                htmlFor={`net_price_${index}`}
                                required
                              >
                                Valor de Custo
                              </RequiredLabel>
                              <FormControl>
                                <RealCurrencyInput
                                  id={`net_price_${index}`}
                                  value={field.value || 0}
                                  onChange={field.onChange}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name={`capital_ranges.${index}.minimum_price`}
                          render={({ field }) => (
                            <FormItem>
                              <RequiredLabel
                                htmlFor={`minimum_price_${index}`}
                                required
                              >
                                Preço Mínimo
                              </RequiredLabel>
                              <FormControl>
                                <RealCurrencyInput
                                  id={`minimum_price_${index}`}
                                  value={field.value || 0}
                                  onChange={field.onChange}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name={`capital_ranges.${index}.maximum_price`}
                          render={({ field }) => {
                            const [isUnlimited, setIsUnlimited] =
                              React.useState(
                                field.value === null || field.value === 0
                              );
                            const initializedRef = React.useRef(false);

                            React.useEffect(() => {
                              if (!initializedRef.current) {
                                initializedRef.current = true;
                                if (field.value === 0) {
                                  field.onChange(null);
                                  setIsUnlimited(true);
                                } else if (field.value === null) {
                                  setIsUnlimited(true);
                                } else {
                                  setIsUnlimited(false);
                                }
                              }
                            }, []);

                            return (
                              <FormItem>
                                <div className="flex items-center justify-between mb-2">
                                  <RequiredLabel
                                    htmlFor={`maximum_price_${index}`}
                                    required
                                  >
                                    Preço Máximo
                                  </RequiredLabel>
                                  <div className="flex items-center gap-2">
                                    <span className="text-xs text-muted-foreground">
                                      Ilimitado
                                    </span>
                                    <Switch
                                      checked={isUnlimited}
                                      onCheckedChange={(checked) => {
                                        setIsUnlimited(checked);
                                        if (checked) {
                                          field.onChange(null);
                                        } else {
                                          field.onChange(0);
                                        }
                                      }}
                                    />
                                  </div>
                                </div>
                                <FormControl>
                                  <RealCurrencyInput
                                    id={`maximum_price_${index}`}
                                    value={field.value || 0}
                                    onChange={field.onChange}
                                    disabled={isUnlimited}
                                  />
                                </FormControl>
                                {isUnlimited && (
                                  <p className="text-xs text-green-600 mt-1">
                                    ✓ Preço máximo ilimitado ativo
                                  </p>
                                )}
                                <FormMessage />
                              </FormItem>
                            );
                          }}
                        />

                        <FormField
                          control={form.control}
                          name={`capital_ranges.${index}.plan_name`}
                          render={({ field }) => (
                            <FormItem>
                              <RequiredLabel
                                htmlFor={`plan_name_${index}`}
                                required={true}
                              >
                                Nome do Plano
                              </RequiredLabel>
                              <FormControl>
                                <Input
                                  {...field}
                                  id={`plan_name_${index}`}
                                  placeholder={t(
                                    "products.form.fields.plan_name_placeholder"
                                  )}
                                  value={field.value || ""}
                                  onChange={(e) => {
                                    field.onChange(e);
                                    validatePlanName(index, e.target.value);
                                  }}
                                  className={
                                    planNameErrors[index]
                                      ? "border-red-500"
                                      : ""
                                  }
                                />
                              </FormControl>
                              <FormMessage />
                              {planNameErrors[index] && (
                                <p className="text-sm text-red-500 mt-1">
                                  {planNameErrors[index]}
                                </p>
                              )}
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name={`capital_ranges.${index}.description`}
                          render={({ field }) => (
                            <FormItem>
                              <RequiredLabel htmlFor={`description_${index}`}>
                                Descrição
                              </RequiredLabel>
                              <FormControl>
                                <Input
                                  {...field}
                                  id={`description_${index}`}
                                  placeholder=" "
                                  value={field.value || ""}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    </Card>
                  ))}
                </div>

                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      addInput();
                    }}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    {t("generic.add")}
                  </Button>

                  {capitalRangeFields.length > 1 && (
                    <Button
                      type="button"
                      variant="destructive"
                      onClick={() => removeInput(capitalRangeFields.length - 1)}
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      {t("generic.removeLast")}
                    </Button>
                  )}
                </div>
              </Card>
            </TabsContent>
            <TabsContent value="integration">
              <Card className="p-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <FormField
                    control={form.control}
                    name="insurance_policy_id"
                    render={({ field }) => (
                      <FormItem>
                        <RequiredLabel htmlFor="insurance_policy_id">
                          {t("products.form.fields.insurance_policy_id")}
                        </RequiredLabel>
                        <FormControl>
                          <Input
                            {...field}
                            id="insurance_policy_id"
                            placeholder=" "
                            value={field.value || ""}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="secondary_policy_holder"
                    render={({ field }) => (
                      <FormItem>
                        <RequiredLabel htmlFor="secondary_policy_holder" required>
                          {t("products.form.fields.secondary_policy_holder")}
                        </RequiredLabel>
                        <FormControl>
                          <Input
                            {...field}
                            id="secondary_policy_holder"
                            placeholder=" "
                            value={field.value || ""}
                            required
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="stipulant_code"
                    render={({ field }) => (
                      <FormItem>
                        <RequiredLabel htmlFor="stipulant_code" required>
                          {t("products.form.fields.stipulant_code")}
                        </RequiredLabel>
                        <FormControl>
                          <Input
                            {...field}
                            id="stipulant_code"
                            placeholder=" "
                            value={field.value || ""}
                            required
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="sub_stipulant_code"
                    render={({ field }) => (
                      <FormItem>
                        <RequiredLabel htmlFor="sub_stipulant_code" required>
                          {t("products.form.fields.sub_stipulant_code")}
                        </RequiredLabel>
                        <FormControl>
                          <Input
                            {...field}
                            id="sub_stipulant_code"
                            placeholder=" "
                            value={field.value || ""}
                            required
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="table_code"
                    render={({ field }) => (
                      <FormItem>
                        <RequiredLabel htmlFor="table_code" required>
                          {t("products.form.fields.table_code")}
                        </RequiredLabel>
                        <FormControl>
                          <Input
                            {...field}
                            id="table_code"
                            placeholder=" "
                            value={field.value || ""}
                            required
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <div className="flex justify-end">
                  <Button className="mt-8" type="submit">
                    {isEditMode
                      ? t("products.button.update")
                      : t("products.button.create")}
                  </Button>
                </div>
              </Card>
            </TabsContent>
          </Tabs>
        </form>
      </Form>
    );
  }
};
